/**
 * SANAL HESAPLAR — geriye dönük deneme (back-test; kullanıcı kararları
 * 14.10.2026, `claude/notlar/2026-10-14-back-test-kararlar.md`).
 *
 * Bir sanal hesap bir KURALDIR: varlıklar ve oranları, başlangıç, para akışı
 * (düzenli ya da gerçek hesapların para akışı), komisyon, temettü kullanımı,
 * dengeleme ve alım gününe rastlantısal kayma. Hareketler kuraldan her
 * seferinde Worker'da türetilir; deftere yazılmaz, elle düzenlenemez.
 * Değerlemesi gerçek hesaplarınkiyle aynı motor: Varlık Dökümü ve Günlük
 * Analiz'de "Hesap" seçicisinden açılır.
 *
 * Kural profilin şifreli kabında durur (yedeğe ve eşitlemeye girer). Merkeze
 * yalnızca seçilen varlıkların fiyat isteği gider (kimlik + tarih aralığı).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import type { TrackedListItemDto } from "@parafon/contracts/central-api";
import {
  HESAP_RENKLERI,
  KUR_KIYASLARI,
  KUR_KIYAS_ONEKI,
  SANAL_MAX_VARLIK,
  kesirliVarsayilan,
  type Account,
  type SanalHesapGirdisi,
  type SanalHesapTanimi,
} from "@parafon/contracts/local-api";
import { useCentral } from "../central/CentralContext";
import { kapsamBellegi } from "../lib/hesapKapsami";
import { routeHref } from "../lib/router";
import { kuralOzeti, sanalVeriEsitle } from "../lib/sanalHesap";
import {
  bosForm,
  farkliKaydetGirdisi,
  formuDonustur,
  kopyaFormu,
  tanimdanForm,
  type FormVarlik,
  type SanalForm,
} from "../lib/sanalForm";
import { esitDagit, toplam, yuzdeYaz } from "../lib/sepetAgirlik";
import { assetClassLabel, formatIsoDate } from "../lib/format";
import { defterDegisti } from "../lib/seriOnbellegi";
import { bugunYerelIso } from "../lib/tarihDonemleri";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import { CurrencySelect } from "../ui/CurrencySelect";
import {
  Button,
  DateInput,
  EmptyState,
  ErrorBlock,
  Field,
  InlineError,
  LoadingBlock,
  Notice,
  Screen,
  Select,
  TextInput,
  cx,
} from "../ui/primitives";
import { SABIT_NOT } from "../components/SanalNotlar";
import { readFileAsText } from "../lib/files";
import { GelenPaylasim, PaylasimPaneli } from "../components/SanalPaylasim";
import {
  PAYLASIM_DOSYA_UZANTISI,
  bekleyenPaylasimiAl,
  benzersizAd,
  linkParametresiniOku,
  paylasimiOku,
  paylasimiYakala,
} from "../lib/sanalPaylasim";
import type { PaylasilanKural } from "../lib/sanalPaylasim";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { KilitSimgesi, KilitliKabiliyet } from "../kabiliyet/KilitliKabiliyet";
import { sanalKilidi } from "../kabiliyet/sanalSinir";
import { M } from "../dil";

/** Komisyon tutarlarının olası para birimleri (hesabınkinden farklı seçilirse). */
const KOMISYON_PARALARI = ["TRY", "USD", "EUR", "GBP"] as const;

const RENK_AD: Readonly<Record<string, string>> = M.sanal.renk;

/** Döviz "varlıkları" — sunucuda araç değil, kur önbelleğinden (Dönemsel Getiri'deki gibi). */
const DOVIZ_OGELERI: readonly TrackedListItemDto[] = KUR_KIYASLARI.map((k) => ({
  instrumentId: `${KUR_KIYAS_ONEKI}${k.para}`,
  symbol: `${k.para}/TL`,
  exchange: "TCMB",
  assetClass: "fx",
  currency: k.para,
  name: M.ortak.kurKiyasAdi[k.para] ?? k.ad,
  state: "ready",
  horizonFrom: "2016-01-01",
  horizonLimitedBy: "source",
  backfilledTo: null,
}));

export function SanalHesaplarScreen() {
  const { repo, revision, invalidate } = useRepositoryContext();
  const merkez = useCentral();
  const port = repo.sanalHesaplar;
  const listeYukle = useCallback(async () => {
    const [sanallar, hesaplar] = await Promise.all([port?.liste() ?? Promise.resolve([]), repo.accounts.list()]);
    return { sanallar, hesaplar };
  }, [repo, port]);
  const { state, reload } = useAsync(listeYukle, [repo, revision]);
  // Gelir modeli: sanal hesap sayısı, B tipi, dengeleme, paylaşım.
  const sinir = useSinirlar();
  const sanalSayisi = state.data?.sanallar.length ?? 0;
  const yeniSanal = sinir.sanalHesap === null || sanalSayisi < sinir.sanalHesap;
  const [kilit, setKilit] = useState<string | null>(null);
  // Kopyala: hak yoksa ya da kural kilitli bir seçenek içeriyorsa kopyalanmaz,
  // kilit ifadesi (Abonelik bağlantısıyla) gösterilir (karar 01.11.2026).
  const [kopyaKilit, setKopyaKilit] = useState<string | null>(null);

  // İzlenen araçların listesi (künye): ad, sınıf, para birimi.
  const [araclar, setAraclar] = useState<readonly TrackedListItemDto[] | null>(null);
  const [aracHatasi, setAracHatasi] = useState<unknown>(null);
  useEffect(() => {
    if (merkez.central === null) {
      setAraclar(DOVIZ_OGELERI);
      return undefined;
    }
    let iptal = false;
    merkez.central
      .trackedList()
      .then((r) => {
        if (!iptal) setAraclar([...DOVIZ_OGELERI, ...r.items]);
      })
      .catch((e: unknown) => {
        if (!iptal) {
          setAracHatasi(e);
          setAraclar(DOVIZ_OGELERI);
        }
      });
    return () => {
      iptal = true;
    };
  }, [merkez.central]);
  const kunye = useMemo(() => new Map((araclar ?? []).map((a) => [a.instrumentId, a])), [araclar]);

  const [form, setForm] = useState<SanalForm | null>(null);
  const [silinecek, setSilinecek] = useState<string | null>(null);
  const [islemHatasi, setIslemHatasi] = useState<unknown>(null);
  const [mesaj, setMesaj] = useState<string | null>(null);

  // PAYLAŞIM (19.10.2026): açık panel (kural kimliği) ve dışarıdan gelen kural.
  const [paylasilan, setPaylasilan] = useState<string | null>(null);
  const [gelen, setGelen] = useState<PaylasilanKural | null>(null);
  const [gelenHata, setGelenHata] = useState<string | null>(null);
  const [ekleniyor, setEkleniyor] = useState(false);
  const gelenAl = useCallback((metin: string, oku: (m: string) => PaylasilanKural) => {
    setMesaj(null);
    try {
      setGelen(oku(metin));
      setGelenHata(null);
    } catch (e: unknown) {
      setGelen(null);
      setGelenHata(e instanceof Error ? e.message : String(e));
    }
  }, []);
  useEffect(() => {
    // Link: uygulama kalkarken (kilitliyken de) belleğe alınmış olabilir; bu
    // sekme zaten açıkken yapıştırılan link de `hashchange` ile gelir.
    const bak = (): void => {
      paylasimiYakala();
      const d = bekleyenPaylasimiAl();
      if (d !== null) gelenAl(d, linkParametresiniOku);
    };
    bak();
    globalThis.addEventListener?.("hashchange", bak);
    return () => globalThis.removeEventListener?.("hashchange", bak);
  }, [gelenAl]);

  if (port === undefined) {
    return (
      <Screen title={M.sanal.ekran.baslik} description={M.sanal.ekran.kisaAciklama}>
        <EmptyState title={M.sanal.ekran.derlemedeYok} description={M.sanal.ekran.derlemedeYokAciklama} />
      </Screen>
    );
  }

  const yenile = () => {
    defterDegisti();
    invalidate();
    reload();
  };

  const kaydet = async (girdi: Parameters<typeof port.kaydet>[0]) => {
    setIslemHatasi(null);
    setKopyaKilit(null);
    setMesaj(null);
    const onceki =
      girdi.id === undefined || girdi.id === "" ? null : (state.data?.sanallar.find((x) => x.id === girdi.id) ?? null);
    const engel = sanalKilidi(girdi, sinir, sanalSayisi, onceki);
    setKilit(engel);
    if (engel !== null) return;
    try {
      const t = await port.kaydet(girdi);
      setForm(null);
      setMesaj(M.sanal.ekran.kaydedildiFiyatlar(t.ad));
      yenile();
      const e = await sanalVeriEsitle(repo, merkez, t);
      setMesaj(
        e.uyarilar.length > 0
          ? M.sanal.ekran.kaydedildiUyari(t.ad, e.uyarilar.join("; "))
          : M.sanal.ekran.kaydedildi(t.ad),
      );
      yenile();
    } catch (err) {
      setIslemHatasi(err);
    }
  };

  const sil = async (id: string) => {
    setIslemHatasi(null);
    try {
      await port.sil(id);
      setSilinecek(null);
      if (kapsamBellegi.rapor() === id || kapsamBellegi.grafik().includes(id)) kapsamBellegi.sifirla();
      yenile();
    } catch (err) {
      setIslemHatasi(err);
    }
  };

  const kopyaKilidi = (t: SanalHesapTanimi): string | null =>
    sanalKilidi({ id: null, paraAkisi: t.paraAkisi, dengeleme: t.dengeleme }, sinir, sanalSayisi);
  const kopyala = (t: SanalHesapTanimi) => {
    setMesaj(null);
    setKilit(null);
    const engel = kopyaKilidi(t);
    setKopyaKilit(engel);
    if (engel !== null) return;
    setForm(kopyaFormu(tanimdanForm(t, kesirliOf, adOf), state.data?.sanallar ?? []));
  };
  const farkliKaydet = (g: SanalHesapGirdisi) => {
    const sanallar = state.data?.sanallar ?? [];
    const asil = sanallar.find((x) => x.id === g.id) ?? null;
    void kaydet(farkliKaydetGirdisi(g, asil, sanallar));
  };

  const yenidenKaristir = (t: SanalHesapTanimi) => {
    const { id, tohum: _eski, ...geri } = t;
    void kaydet({ ...geri, id });
  };

  const gelenAd = gelen === null ? "" : benzersizAd(gelen.ad, (state.data?.sanallar ?? []).map((t) => t.ad));
  const gelenKilidi = gelen === null ? null : sanalKilidi({ id: null, paraAkisi: gelen.paraAkisi, dengeleme: gelen.dengeleme }, sinir, sanalSayisi);
  const gelenEkle = (): void => {
    if (gelen === null || gelenKilidi !== null) return;
    if (gelen.paraAkisi.tur === "defter") {
      // B: para akışı alıcının hesaplarına bağlanmadan kaydedilemez; form
      // doldurulmuş açılır, hesap seçimi (ya da düzenli akış) kullanıcının.
      const t = { ...gelen, ad: gelenAd, id: "", paraAkisi: { tur: "defter" as const, accountIds: [] } };
      setForm({ ...tanimdanForm(t as SanalHesapTanimi, kesirliOf, adOf), id: null });
      setGelen(null);
      return;
    }
    setEkleniyor(true);
    void kaydet({ ...gelen, ad: gelenAd } as SanalHesapGirdisi).finally(() => {
      setEkleniyor(false);
      setGelen(null);
    });
  };
  const dosyadanAl = (f: File | undefined): void => {
    if (f === undefined) return;
    void readFileAsText(f).then(
      (m) => gelenAl(m, paylasimiOku),
      (e: unknown) => setGelenHata(e instanceof Error ? e.message : String(e)),
    );
  };

  const kesirliOf = (id: string): boolean => {
    const k = kunye.get(id);
    return kesirliVarsayilan(k?.assetClass ?? null, k?.currency ?? null);
  };
  const adOf = (id: string): string => kunye.get(id)?.symbol ?? id;

  return (
    <Screen
      title={M.sanal.ekran.baslik}
      description={M.sanal.ekran.aciklama}
      toolbar={
        form === null ? (
          <div className="flex flex-wrap gap-2">
            <label className={cx("pf-btn pf-btn-ghost", yeniSanal ? "cursor-pointer" : "pointer-events-none opacity-50")} aria-disabled={!yeniSanal}>
              {M.sanal.ekran.dosyadanAl}
              <input
                type="file"
                disabled={!yeniSanal}
                accept={`${PAYLASIM_DOSYA_UZANTISI},application/json,.json`}
                className="sr-only"
                onChange={(e) => {
                  dosyadanAl(e.currentTarget.files?.[0]);
                  e.currentTarget.value = "";
                }}
              />
            </label>
            <Button variant="primary" disabled={!yeniSanal} onClick={() => setForm(bosForm(bugunYerelIso()))}>
              {M.sanal.ekran.yeni}
            </Button>
          </div>
        ) : null
      }
    >
      <div className="space-y-4 p-4">
        <p className="text-[12px] text-muted">{SABIT_NOT}</p>
        {!yeniSanal && form === null && (
          <KilitliKabiliyet
            ne={sinir.sanalHesap === 0 ? M.ayarlar.kilit.sanalHesap : M.ayarlar.kilit.sanalFazlasi(sinir.sanalHesap ?? 0)}
            {...(sanalSayisi > 0 ? { aciklama: M.sanal.ekran.varOlanlarSilinmedi } : {})}
          />
        )}
        {kilit !== null && <KilitliKabiliyet ne={kilit} aciklama={M.sanal.ekran.kuralKaydedilmedi} />}
        {kopyaKilit !== null && form === null && <KilitliKabiliyet ne={kopyaKilit} />}
        {mesaj !== null && <p role="status" className="text-[13px] text-ink">{mesaj}</p>}
        {islemHatasi !== null && <InlineError error={islemHatasi} />}
        {gelenHata !== null && (
          <Notice tone="warn" title={M.sanal.ekran.paylasilanEklenmedi}>
            <p>{gelenHata}</p>
          </Notice>
        )}
        {gelen !== null && form === null && (
          <GelenPaylasim
            kural={gelen}
            ad={gelenAd}
            bekliyor={ekleniyor || state.data === undefined}
            kilit={state.data === undefined ? null : gelenKilidi}
            onEkle={gelenEkle}
            onVazgec={() => setGelen(null)}
          />
        )}
        {form !== null && (
          <SanalHesapFormu
            form={form}
            onChange={setForm}
            onIptal={() => setForm(null)}
            onKaydet={(g) => void kaydet(g)}
            onFarkliKaydet={farkliKaydet}
            farkliKilitli={
              !yeniSanal ||
              (form.akis === "defter" && !sinir.sanalB) ||
              (form.dengeleme !== "yok" && !sinir.sanalDengeleme)
            }
            araclar={araclar}
            aracHatasi={aracHatasi}
            hesaplar={state.data?.hesaplar ?? []}
            kabiliyet={{ b: sinir.sanalB, dengeleme: sinir.sanalDengeleme }}
          />
        )}
        {state.status === "error" && <ErrorBlock error={state.error} onRetry={reload} />}
        {state.data === undefined && state.status === "loading" && <LoadingBlock label={M.sanal.ekran.okunuyor} />}
        {state.data !== undefined && state.data.sanallar.length === 0 && form === null && (
          <EmptyState
            title={M.sanal.ekran.bosBaslik}
            description={M.sanal.ekran.bosAciklama}
          />
        )}
        {state.data !== undefined && state.data.sanallar.length > 0 && (
          <ul className="space-y-2" aria-label={M.sanal.ekran.liste}>
            {state.data.sanallar.map((t) => (
              <li key={t.id} className="pf-card flex flex-wrap items-start justify-between gap-3 p-3" data-kart-bolum="liste">
                <div className="flex min-w-0 gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 inline-block h-9 w-1 shrink-0 rounded"
                    style={{ background: t.renk === undefined ? "var(--pf-border)" : `var(--pf-hesap-${t.renk})` }}
                  />
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">{t.ad}</p>
                    <p className="text-[12px] text-muted">{kuralOzeti(t)}</p>
                    <p className="text-[12px] text-muted">
                      {formatIsoDate(t.baslangic)} – {t.bitis == null ? M.sanal.ekran.bugun : formatIsoDate(t.bitis)} ·{" "}
                      {t.varliklar.map((v) => adOf(v.instrumentId)).join(", ")}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  <a className="pf-btn pf-btn-ghost" href={routeHref("rapor")} onClick={() => kapsamBellegi.ac(t.id)}>
                    {M.sanal.ekran.varlikDokumu}
                  </a>
                  <a className="pf-btn pf-btn-ghost" href={routeHref("grafik")} onClick={() => kapsamBellegi.ac(t.id)}>
                    {M.sanal.ekran.gunlukAnaliz}
                  </a>
                  <Button onClick={() => setForm(tanimdanForm(t, kesirliOf, adOf))}>{M.sanal.ekran.duzenle}</Button>
                  <Button onClick={() => kopyala(t)}>
                    {kopyaKilidi(t) !== null && <KilitSimgesi className="mr-1" />}
                    {M.sanal.ekran.kopyala}
                  </Button>
                  <Button
                    aria-expanded={paylasilan === t.id}
                    disabled={!sinir.sanalPaylasim}
                    title={sinir.sanalPaylasim ? undefined : M.sanal.ekran.paylasimUcretli}
                    onClick={() => setPaylasilan((p) => (p === t.id ? null : t.id))}
                  >
                    {M.sanal.ekran.paylas}
                  </Button>
                  {(t.kayma.geri > 0 || t.kayma.ileri > 0) && (
                    <Button onClick={() => yenidenKaristir(t)}>{M.sanal.ekran.yenidenKaristir}</Button>
                  )}
                  {silinecek === t.id ? (
                    <>
                      <Button variant="danger" onClick={() => void sil(t.id)}>
                        {M.sanal.ekran.silmeyiOnayla}
                      </Button>
                      <Button onClick={() => setSilinecek(null)}>{M.sanal.ekran.vazgec}</Button>
                    </>
                  ) : (
                    <Button onClick={() => setSilinecek(t.id)}>{M.sanal.ekran.sil}</Button>
                  )}
                </div>
                {paylasilan === t.id && <PaylasimPaneli tanim={t} />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

function Radyo<T extends string>({
  ad,
  baslik,
  deger,
  secenekler,
  onChange,
  kilitli = [],
}: {
  readonly ad: string;
  readonly baslik: string;
  readonly deger: T;
  readonly secenekler: readonly (readonly [T, string])[];
  readonly onChange: (v: T) => void;
  /** Gelir modeli: seçilemeyen (ücretli) seçenekler; seçiliyse seçili kalır. */
  readonly kilitli?: readonly T[];
}) {
  return (
    <fieldset className="space-y-1">
      <legend className="text-[12px] font-semibold text-muted">{baslik}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {secenekler.map(([v, etiket]) => (
          <label key={v} className="flex items-center gap-1.5 text-[13px]">
            <input
              type="radio"
              name={ad}
              value={v}
              checked={deger === v}
              disabled={kilitli.includes(v) && deger !== v}
              onChange={() => onChange(v)}
            />
            {etiket}
            {kilitli.includes(v) && <KilitSimgesi className="text-muted" />}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function SanalHesapFormu({
  form,
  onChange,
  onIptal,
  onKaydet,
  onFarkliKaydet,
  farkliKilitli = false,
  araclar,
  aracHatasi,
  hesaplar,
  kabiliyet = { b: true, dengeleme: true },
}: {
  /** Yalnızca düzenlemede: değiştirilmiş hâl yeni sanal hesap olur. */
  readonly onFarkliKaydet?: (g: Extract<ReturnType<typeof formuDonustur>, { ok: true }>["girdi"]) => void;
  /** Yeni sanal hesap hakkı ya da kuraldaki seçenek kilitliyse düğmede kilit (kayıt yine kilit ifadesiyle durur). */
  readonly farkliKilitli?: boolean;
  readonly kabiliyet?: { readonly b: boolean; readonly dengeleme: boolean };
  readonly form: SanalForm;
  readonly onChange: (f: SanalForm) => void;
  readonly onIptal: () => void;
  readonly onKaydet: (g: Extract<ReturnType<typeof formuDonustur>, { ok: true }>["girdi"]) => void;
  readonly araclar: readonly TrackedListItemDto[] | null;
  readonly aracHatasi: unknown;
  readonly hesaplar: readonly Account[];
}) {
  const [hatalar, setHatalar] = useState<Readonly<Record<string, string>>>({});
  const hataOf = (k: string): { error?: string } => (hatalar[k] === undefined ? {} : { error: hatalar[k] as string });
  const [arama, setArama] = useState("");
  const f = form;
  const set = (p: Partial<SanalForm>) => onChange({ ...f, ...p });
  const setVarlik = (i: number, p: Partial<FormVarlik>) =>
    set({ varliklar: f.varliklar.map((v, k) => (k === i ? { ...v, ...p } : v)) });

  const adaylar = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr");
    if (q === "" || araclar === null) return [];
    return araclar
      .filter((a) => !f.varliklar.some((v) => v.instrumentId === a.instrumentId))
      .filter((a) => a.symbol.toLocaleLowerCase("tr").includes(q) || a.name.toLocaleLowerCase("tr").includes(q))
      .slice(0, 8);
  }, [arama, araclar, f.varliklar]);

  const ekle = (a: TrackedListItemDto) => {
    const vars = kesirliVarsayilan(a.assetClass, a.currency);
    set({
      varliklar: [
        ...f.varliklar,
        { instrumentId: a.instrumentId, ad: a.symbol, oran: "", kesirliVarsayilan: vars, kesirli: vars, kilitli: false },
      ],
    });
    setArama("");
  };

  const toplamOran = toplam(f.varliklar.map((v) => v.oran));
  const gonder = (farkli = false) => {
    const r = formuDonustur(f);
    if (!r.ok) {
      setHatalar(r.hatalar);
      return;
    }
    setHatalar({});
    if (farkli && onFarkliKaydet !== undefined) onFarkliKaydet(r.girdi);
    else onKaydet(r.girdi);
  };

  return (
    <section aria-label={f.id === null ? M.sanal.form.yeni : M.sanal.form.duzenleEtiket} className="pf-card space-y-4 p-4" data-kart-bolum="form">
      <h2 className="text-[15px] font-semibold text-ink">{f.id === null ? M.sanal.form.yeni : M.sanal.form.duzenleBaslik(f.ad)}</h2>

      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={M.sanal.form.ad} htmlFor="sanal-ad" {...hataOf("ad")} required>
          <TextInput id="sanal-ad" value={f.ad} maxLength={60} onChange={(e) => set({ ad: e.currentTarget.value })} />
        </Field>
        <Field label={M.sanal.form.renk} htmlFor="sanal-renk">
          <Select
            id="sanal-renk"
            value={f.renk}
            placeholder={M.sanal.form.renksiz}
            options={HESAP_RENKLERI.map((r) => ({ value: r, label: RENK_AD[r] ?? r }))}
            onChange={(e) => set({ renk: e.currentTarget.value as SanalForm["renk"] })}
          />
        </Field>
        <Field label={M.sanal.form.paraBirimi} htmlFor="sanal-para" hint={M.sanal.form.paraBirimiIpucu}>
          <CurrencySelect id="sanal-para" value={f.paraBirimi} onChange={(v) => set({ paraBirimi: v })} />
        </Field>
      </div>

      {/* --- varlıklar --- */}
      <fieldset className="space-y-2">
        <legend className="text-[13px] font-semibold text-ink">{M.sanal.form.varliklar}</legend>
        {aracHatasi !== null && (
          <Notice tone="warn" title={M.sanal.form.listeAlinamadi}>
            {M.sanal.form.listeAlinamadiMetin}
          </Notice>
        )}
        {f.varliklar.length > 0 && (
          <table className="w-full text-[13px]">
            <thead className="text-left text-[12px] text-muted">
              <tr>
                <th className="py-1">{M.sanal.form.varlik}</th>
                <th className="py-1">{M.sanal.form.oran}</th>
                <th className="py-1" title={M.sanal.form.kusuratIpucu}>
                  {M.sanal.form.kusurat}
                </th>
                {f.dengeleme === "satarak" && (
                  <th className="py-1" title={M.sanal.form.kilitliIpucu}>
                    {M.sanal.form.kilitli}
                  </th>
                )}
                <th className="py-1">
                  <span className="sr-only">{M.sanal.form.cikar}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {f.varliklar.map((v, i) => (
                <tr key={v.instrumentId} className="border-t border-line">
                  <td className="py-1 pr-2">{v.ad}</td>
                  <td className="py-1 pr-2">
                    <TextInput
                      aria-label={M.sanal.form.oranEtiket(v.ad)}
                      numeric
                      className="w-20"
                      value={v.oran}
                      onChange={(e) => setVarlik(i, { oran: e.currentTarget.value })}
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      type="checkbox"
                      aria-label={M.sanal.form.kusuratEtiket(v.ad)}
                      checked={v.kesirli}
                      onChange={(e) => setVarlik(i, { kesirli: e.currentTarget.checked })}
                    />
                  </td>
                  {f.dengeleme === "satarak" && (
                    <td className="py-1 pr-2">
                      <input
                        type="checkbox"
                        aria-label={M.sanal.form.kilitliEtiket(v.ad)}
                        checked={v.kilitli}
                        onChange={(e) => setVarlik(i, { kilitli: e.currentTarget.checked })}
                      />
                    </td>
                  )}
                  <td className="py-1 text-right">
                    <Button onClick={() => set({ varliklar: f.varliklar.filter((_, k) => k !== i) })}>{M.sanal.form.cikar}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="flex flex-wrap items-center gap-2 text-[12px]">
          <span className={cx(toplamOran === 10_000 ? "text-muted" : "text-ink")}>
            {M.sanal.form.toplam} {toplamOran === null ? "—" : M.bicim.yuzde(yuzdeYaz(toplamOran))}
          </span>
          {f.varliklar.length > 0 && (
            <Button
              onClick={() => {
                const es = esitDagit(f.varliklar.length);
                set({ varliklar: f.varliklar.map((v, i) => ({ ...v, oran: es[i] ?? "" })) });
              }}
            >
              {M.sanal.form.esitDagit}
            </Button>
          )}
        </div>
        {hatalar["varliklar"] !== undefined && <p className="text-[12px] text-[var(--pf-danger)]">{hatalar["varliklar"]}</p>}
        {f.varliklar.length < SANAL_MAX_VARLIK && (
          <div className="relative max-w-md">
            <Field label={M.sanal.form.varlikEkle} htmlFor="sanal-arama" hint={M.sanal.form.varlikEkleIpucu}>
              <TextInput id="sanal-arama" value={arama} onChange={(e) => setArama(e.currentTarget.value)} />
            </Field>
            {adaylar.length > 0 && (
              <ul className="mt-1 rounded-[var(--pf-radius-sm)] border border-line bg-surface" aria-label={M.sanal.form.bulunanlar}>
                {adaylar.map((a) => (
                  <li key={a.instrumentId}>
                    <button
                      type="button"
                      className="flex w-full justify-between gap-2 px-2 py-1 text-left text-[13px] hover:bg-accent-soft"
                      onClick={() => ekle(a)}
                    >
                      <span>
                        <strong>{a.symbol}</strong> {a.name}
                      </span>
                      <span className="text-faint">
                        {assetClassLabel(a.assetClass)} · {a.currency}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </fieldset>

      {/* --- tarihler --- */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label={M.sanal.form.baslangic} htmlFor="sanal-bas" {...hataOf("baslangic")} required hint={M.sanal.form.baslangicIpucu}>
          <DateInput id="sanal-bas" value={f.baslangic} max={bugunYerelIso()} onChange={(e) => set({ baslangic: e.currentTarget.value })} />
        </Field>
        <Field label={M.sanal.form.bitis} htmlFor="sanal-bit" {...hataOf("bitis")} hint={M.sanal.form.bitisIpucu}>
          <DateInput id="sanal-bit" value={f.bitis} max={bugunYerelIso()} onChange={(e) => set({ bitis: e.currentTarget.value })} />
        </Field>
      </div>

      {/* --- para akışı --- */}
      <Radyo
        ad="sanal-akis"
        baslik={M.sanal.form.paraAkisi}
        deger={f.akis}
        secenekler={[
          ["duzenli", M.sanal.form.duzenliAlim],
          ["defter", M.sanal.form.defterAkisi],
        ]}
        kilitli={kabiliyet.b ? [] : ["defter"]}
        onChange={(akis) => set({ akis })}
      />
      {f.akis === "duzenli" ? (
        <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-4">
            <Field label={M.sanal.form.baslangicTutari} htmlFor="sanal-bt" {...hataOf("baslangicTutari")}>
              <TextInput id="sanal-bt" numeric value={f.baslangicTutari} onChange={(e) => set({ baslangicTutari: e.currentTarget.value })} />
            </Field>
            <Field label={M.sanal.form.periyotTutari} htmlFor="sanal-pt" {...hataOf("periyotTutari")} hint={M.sanal.form.periyotTutariIpucu}>
              <TextInput id="sanal-pt" numeric value={f.periyotTutari} onChange={(e) => set({ periyotTutari: e.currentTarget.value })} />
            </Field>
            <Field label={M.sanal.form.periyot} htmlFor="sanal-adim" {...hataOf("periyot")}>
              <div className="flex gap-1.5">
                <TextInput id="sanal-adim" numeric className="w-16" value={f.periyotAdim} onChange={(e) => set({ periyotAdim: e.currentTarget.value })} />
                <Select
                  aria-label={M.sanal.form.periyotBirimi}
                  value={f.periyotBirim}
                  options={[
                    { value: "gun", label: M.sanal.form.gunde },
                    { value: "hafta", label: M.sanal.form.haftada },
                    { value: "ay", label: M.sanal.form.ayda },
                  ]}
                  onChange={(e) => set({ periyotBirim: e.currentTarget.value as SanalForm["periyotBirim"] })}
                />
              </div>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <input type="checkbox" checked={f.cekimAcik} onChange={(e) => set({ cekimAcik: e.currentTarget.checked })} />
            {M.sanal.form.duzenliCekim}
          </label>
          {f.cekimAcik && (
            <div className="grid gap-3 sm:grid-cols-4">
              <Field label={M.sanal.form.cekimTutari} htmlFor="sanal-ct" {...hataOf("cekim")}>
                <TextInput id="sanal-ct" numeric value={f.cekimTutari} onChange={(e) => set({ cekimTutari: e.currentTarget.value })} />
              </Field>
              <Field label={M.sanal.form.cekimBaslangici} htmlFor="sanal-cb">
                <DateInput id="sanal-cb" value={f.cekimBaslangic} onChange={(e) => set({ cekimBaslangic: e.currentTarget.value })} />
              </Field>
            </div>
          )}
        </div>
      ) : (
        <fieldset className="space-y-1">
          <legend className="text-[12px] text-muted">
            {M.sanal.form.defterAciklama}
          </legend>
          {hesaplar.filter((a) => !a.deleted).map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={f.defterHesaplari.includes(a.id)}
                onChange={(e) =>
                  set({
                    defterHesaplari: e.currentTarget.checked
                      ? [...f.defterHesaplari, a.id]
                      : f.defterHesaplari.filter((x) => x !== a.id),
                  })
                }
              />
              {a.name}
            </label>
          ))}
          {hatalar["defter"] !== undefined && <p className="text-[12px] text-[var(--pf-danger)]">{hatalar["defter"]}</p>}
        </fieldset>
      )}

      {/* --- komisyon (hesap başına tek kural; kullanıcı kararı 15.10.2026) --- */}
      <fieldset className="space-y-2">
        <legend className="text-[13px] font-semibold text-ink">{M.sanal.form.komisyon}</legend>
        <p className="text-[12px] text-muted">
          {M.sanal.form.komisyonAciklama}
        </p>
        <div className="grid gap-3 sm:grid-cols-5">
          <Field label={M.sanal.form.yuzde} htmlFor="sanal-kom" {...hataOf("komisyon")}>
            <TextInput id="sanal-kom" numeric value={f.komisyon} onChange={(e) => set({ komisyon: e.currentTarget.value })} />
          </Field>
          <Field label={M.sanal.form.sabitTutar} htmlFor="sanal-ksabit" {...hataOf("komisyonSabit")}>
            <TextInput id="sanal-ksabit" numeric value={f.komisyonSabit} onChange={(e) => set({ komisyonSabit: e.currentTarget.value })} />
          </Field>
          <Field label={M.sanal.form.enAz} htmlFor="sanal-kenaz" {...hataOf("komisyonEnAz")}>
            <TextInput id="sanal-kenaz" numeric value={f.komisyonEnAz} onChange={(e) => set({ komisyonEnAz: e.currentTarget.value })} />
          </Field>
          <Field label={M.sanal.form.enCok} htmlFor="sanal-kencok" {...hataOf("komisyonEnCok")}>
            <TextInput id="sanal-kencok" numeric value={f.komisyonEnCok} onChange={(e) => set({ komisyonEnCok: e.currentTarget.value })} />
          </Field>
          <Field label={M.sanal.form.tutarParasi} htmlFor="sanal-kpara" hint={M.sanal.form.tutarParasiIpucu}>
            <Select
              id="sanal-kpara"
              value={f.komisyonParaBirimi}
              placeholder={M.sanal.form.hesabinParasi(f.paraBirimi)}
              options={KOMISYON_PARALARI.filter((p) => p !== f.paraBirimi).map((p) => ({ value: p, label: p }))}
              onChange={(e) => set({ komisyonParaBirimi: e.currentTarget.value })}
            />
          </Field>
        </div>
      </fieldset>

      {/* --- kurallar --- */}
      <div className="grid gap-3 sm:grid-cols-4">
        <Field label={M.sanal.form.kaymaGeri} htmlFor="sanal-kg" {...hataOf("kayma")}>
          <TextInput id="sanal-kg" numeric value={f.kaymaGeri} onChange={(e) => set({ kaymaGeri: e.currentTarget.value })} />
        </Field>
        <Field label={M.sanal.form.kaymaIleri} htmlFor="sanal-ki" hint={M.sanal.form.kaymaIpucu}>
          <TextInput id="sanal-ki" numeric value={f.kaymaIleri} onChange={(e) => set({ kaymaIleri: e.currentTarget.value })} />
        </Field>
      </div>
      <Radyo
        ad="sanal-temettu"
        baslik={M.sanal.form.temettu}
        deger={f.temettu}
        secenekler={[
          ["ayniVarlik", M.sanal.form.ayniVarlik],
          ["sonrakiAlim", M.sanal.form.sonrakiAlim],
          ["yokSay", M.sanal.form.yokSay],
        ]}
        onChange={(temettu) => set({ temettu })}
      />
      {f.temettu === "ayniVarlik" && (
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            checked={f.temettuKomisyonu}
            onChange={(e) => set({ temettuKomisyonu: e.currentTarget.checked })}
          />
          {M.sanal.form.temettuKomisyonu}
        </label>
      )}
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          checked={f.artanSonrakiAlima}
          onChange={(e) => set({ artanSonrakiAlima: e.currentTarget.checked })}
        />
        {M.sanal.form.artanSonrakiAlima}
      </label>
      <Radyo
        ad="sanal-dengeleme"
        baslik={M.sanal.form.dengeleme}
        deger={f.dengeleme}
        secenekler={[
          ["yok", M.sanal.form.dengelemeYok],
          ["alimla", M.sanal.form.dengelemeAlimla],
          ["satarak", M.sanal.form.dengelemeSatarak],
        ]}
        kilitli={kabiliyet.dengeleme ? [] : ["alimla", "satarak"]}
        onChange={(dengeleme) => set({ dengeleme })}
      />
      {f.dengeleme === "satarak" && (
        <Field label={M.sanal.form.esik} htmlFor="sanal-esik" {...hataOf("esik")} className="w-56">
          <TextInput id="sanal-esik" numeric value={f.esik} onChange={(e) => set({ esik: e.currentTarget.value })} />
        </Field>
      )}

      <div className="flex gap-2">
        <Button variant="primary" onClick={() => gonder()}>
          {M.sanal.form.kaydet}
        </Button>
        {f.id !== null && onFarkliKaydet !== undefined && (
          <Button onClick={() => gonder(true)}>
            {farkliKilitli && <KilitSimgesi className="mr-1" />}
            {M.sanal.form.farkliKaydet}
          </Button>
        )}
        <Button onClick={onIptal}>{M.sanal.form.vazgec}</Button>
      </div>
    </section>
  );
}
